'use client';

import { useState } from 'react';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

interface HeroSectionProps {
  onSearch: (query: string) => void;
}

export default function HeroSection({ onSearch }: HeroSectionProps) {
  const [searchQuery, setSearchQuery] = useState('');

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    onSearch(searchQuery);
  };

  const stats = [
  { id: 'stat_books', label: 'Books Reviewed', value: '247', icon: 'BookOpenIcon' },
  { id: 'stat_genres', label: 'Genres Covered', value: '15', icon: 'Squares2X2Icon' },
  { id: 'stat_rating', label: 'Avg Rating', value: '4.2', icon: 'StarIcon' }];


  return (
    <section className="relative min-h-screen flex flex-col justify-center overflow-hidden">
      {/* Background Image */}
      <div className="absolute inset-0 z-0">
        <AppImage
          src="https://images.unsplash.com/photo-1617104833784-3b37a50d1c76"
          alt="Cozy reading nook with vintage books and warm lighting"
          className="w-full h-full object-cover opacity-40" />

        {/* Gradient Overlays */}
        <div className="absolute inset-0 bg-gradient-to-b from-background/70 via-background/40 to-background" />
        <div className="absolute inset-0 bg-gradient-to-r from-background/80 via-transparent to-background/80" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto w-full px-6 pt-32 pb-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-end">
          {/* Main Content */}
          <div className="lg:col-span-8 animate-fade-in-up">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-primary/20 bg-primary/10 backdrop-blur-md text-xs uppercase tracking-widest font-medium text-primary mb-8">
              <span className="w-2 h-2 rounded-full bg-primary animate-pulse-slow" />
              Literary Discoveries
            </div>

            <h1 className="text-5xl md:text-7xl lg:text-8xl font-serif font-semibold tracking-tight text-foreground mb-8 leading-[0.95]">
              Discover Your <br />
              <span className="text-gradient">Next Great Read</span>
            </h1>

            <p className="text-lg md:text-xl text-muted-foreground max-w-2xl font-light leading-relaxed border-l-2 border-primary/30 pl-6 mb-10">
              Honest reviews from a passionate reader's journey through literature. 
              Find your next favorite book across 15+ genres.
            </p>

            {/* Search Bar */}
            <form onSubmit={handleSearch} className="max-w-2xl">
              <div className="relative glass-card rounded-xl overflow-hidden">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search for books, authors, or genres..."
                  className="w-full bg-transparent px-6 py-5 pr-14 text-foreground placeholder-muted-foreground focus:outline-none" />

                <button
                  type="submit"
                  className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-lg bg-primary hover:bg-primary/90 transition-colors flex items-center justify-center"
                  aria-label="Search">

                  <Icon name="MagnifyingGlassIcon" size={20} className="text-primary-foreground" />
                </button>
              </div>
            </form>

            <div className="flex flex-wrap gap-3 mt-6">
              <span className="text-sm text-muted-foreground">Popular:</span>
              {['Fiction', 'Mystery', 'Fantasy', 'Non-Fiction'].map((genre) =>
              <button
                key={`popular_${genre.toLowerCase()}`}
                onClick={() => onSearch(genre)}
                className="px-4 py-1.5 rounded-full border border-border bg-card/50 text-sm text-foreground hover:border-primary hover:bg-primary/10 transition-colors">

                  {genre}
                </button>
              )}
            </div>
          </div>

          {/* Stats Cards */}
          <div className="lg:col-span-4 flex flex-col gap-4 animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
            {stats.map((stat) =>
            <div
              key={stat.id}
              className="glass-card p-6 rounded-xl hover:bg-card/80 transition-colors cursor-default">

                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs text-muted-foreground uppercase tracking-widest font-semibold">
                    {stat.label}
                  </span>
                  <Icon name={stat.icon as any} size={20} className="text-accent" />
                </div>
                <div className="text-4xl font-serif font-light text-foreground">
                  {stat.value}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>);

}